import { useState, useCallback, useRef, useEffect, useMemo, useId } from 'react'
import { TICKER_SEARCH_OPEN_EVENT, type TickerSearchOpenDetail } from './ticker-search-events'
import { createPortal } from 'react-dom'

/** The one ticker search currently open, app-wide. Module singleton — the
 *  chart chip and panel header both render instances; opening one closes the
 *  other so two result dropdowns can't stack. */
let activeTickerSearch: { owner: object; close: () => void } | null = null
import { Search, X } from 'lucide-react'
import {
  searchTickers,
  searchTickersWithFallback,
  isDirectoryLoaded,
} from '@/stores/ticker-directory'
import type { TickerSearchResult } from '@/types/schemas/api'
import { cn } from '@/lib/utils'
import { computeDropdownPosition } from './ticker-search-position'
import type { DropdownPosition } from './ticker-search-position'

interface TickerSearchProps {
  /** Currently selected ticker symbol (e.g., "AAPL"). */
  value?: string
  /** Called when a ticker is selected. */
  onSelect: (symbol: string, name: string) => void
  /** Placeholder text. */
  placeholder?: string
  /** Additional CSS classes. */
  className?: string
  /** Render as compact inline trigger vs full-width input. */
  variant?: 'inline' | 'full'
  /** Show a clear (X) button on the pill. */
  showClear?: boolean
  /** Called when the clear button is clicked. */
  onClear?: () => void
  /** Value for data-companion-label attribute. */
  companionLabel?: string
  /** Open the field in edit mode on first render. */
  autoFocus?: boolean
  /**
   * Opt-in ThinkorSwim-style invert-scale shortcut: a leading `-` (e.g.
   * `-AAPL`) is stripped before searching/selecting, so `onSelect` always
   * receives the clean symbol. Pair with `onInvertPrefixChange` to read
   * whether the dash was present. Chart ticker inputs only — never the
   * global top-bar search — so default is off (today's exact behavior).
   */
  invertPrefix?: boolean
  /**
   * Fires alongside `onSelect` when `invertPrefix` is set, with whether the
   * typed query had a leading `-`. Ignored when `invertPrefix` is unset.
   */
  onInvertPrefixChange?: (inverted: boolean) => void
}

/**
 * Ticker search combobox backed by the in-memory ticker directory.
 *
 * Click the pill to enter edit mode — the label becomes an inline text input.
 * Results appear in a portal-based dropdown below. Escape or click-outside closes.
 */
export function TickerSearch({
  value,
  onSelect,
  placeholder = 'Ticker',
  className,
  variant = 'inline',
  showClear,
  onClear,
  companionLabel,
  autoFocus = false,
  invertPrefix = false,
  onInvertPrefixChange,
}: TickerSearchProps) {
  const [editing, setEditing] = useState(autoFocus)
  const [query, setQuery] = useState('')
  const [highlightIndex, setHighlightIndex] = useState(0)
  const listboxId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const [debouncedQuery, setDebouncedQuery] = useState('')
  // Whether the search is open, and the query a keyboard-layer open has built so far: both
  // move at the event, not at the next render, since several keys can arrive in between.
  const editingRef = useRef(editing)
  editingRef.current = editing
  const seedQueryRef = useRef('')
  // Where the pointer last moved over the list: a row takes the highlight only when the
  // pointer actually moves, not when the list opens under a resting pointer.
  const pointerRef = useRef<{ x: number; y: number } | null>(null)

  const handleSearch = useCallback(
    (raw: string) => {
      // Tickers are uppercase: capitalize as typed so the input always shows
      // the symbol the way it will commit (select paths already uppercase).
      raw = raw.toUpperCase()
      setQuery(raw)
      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        const trimmed = raw.trim()
        // Strip a single leading '-' before matching so autocomplete still
        // finds the clean ticker — the dash never reaches searchTickers.
        const forSearch = invertPrefix && trimmed.startsWith('-') ? trimmed.slice(1) : trimmed
        setDebouncedQuery(forSearch)
      }, 100)
    },
    [invertPrefix],
  )

  useEffect(() => {
    return () => clearTimeout(debounceRef.current)
  }, [])

  const localResults = useMemo(() => searchTickers(debouncedQuery, 7), [debouncedQuery])
  const [fallbackState, setFallbackState] = useState<{
    query: string
    results: TickerSearchResult[]
  }>({ query: '', results: [] })
  const normalizedQuery = query.trim().toUpperCase()
  const searchQuery =
    invertPrefix && normalizedQuery.startsWith('-') ? normalizedQuery.slice(1) : normalizedQuery

  useEffect(() => {
    if (!editing || !debouncedQuery || searchQuery !== debouncedQuery || localResults.length > 0)
      return
    let current = true
    void searchTickersWithFallback(debouncedQuery, 7).then(
      (results) => {
        if (current) {
          setFallbackState({ query: debouncedQuery, results })
        }
      },
      () => {
        if (current) {
          setFallbackState({ query: debouncedQuery, results: [] })
        }
      },
    )
    return () => {
      current = false
    }
  }, [debouncedQuery, editing, localResults.length, searchQuery])

  const results =
    searchQuery !== debouncedQuery
      ? []
      : localResults.length > 0 || fallbackState.query !== debouncedQuery
        ? localResults
        : fallbackState.results
  const loaded = isDirectoryLoaded()

  // Reset highlight when results change
  useEffect(() => {
    setHighlightIndex(0)
  }, [debouncedQuery])

  const close = useCallback(() => {
    pointerRef.current = null
    setEditing(false)
    setQuery('')
    setDebouncedQuery('')
    if (activeTickerSearch?.owner === ownerRef.current) activeTickerSearch = null
  }, [])

  // Single-open rule: the chart chip and the panel header can each render a
  // TickerSearch for the same panel — opening one closes any other.
  const ownerRef = useRef({})
  const closeRef = useRef(close)
  closeRef.current = close
  useEffect(
    () => () => {
      if (activeTickerSearch?.owner === ownerRef.current) activeTickerSearch = null
    },
    [],
  )

  const selectTicker = useCallback(
    (symbol: string) => {
      const match = results.find((r) => r.symbol === symbol)
      onSelect(symbol, match?.name ?? '')
      if (invertPrefix) onInvertPrefixChange?.(query.trim().startsWith('-'))
      close()
    },
    [results, onSelect, close, invertPrefix, onInvertPrefixChange, query],
  )

  // True while the current edit session was opened pre-seeded (keyboard
  // layer): the seed must survive focus, so the focus effect places the caret
  // at the end instead of select-all (which the next keystroke would wipe).
  const seededRef = useRef(false)

  const startEditing = useCallback(() => {
    if (activeTickerSearch && activeTickerSearch.owner !== ownerRef.current) {
      activeTickerSearch.close()
    }
    activeTickerSearch = { owner: ownerRef.current, close: () => closeRef.current() }
    seededRef.current = false
    setEditing(true)
    setQuery(value ?? '')
    setDebouncedQuery(value ?? '')
  }, [value])
  const startEditingRef = useRef(startEditing)
  startEditingRef.current = startEditing

  // Keyboard-layer open: the chart's bare-letter hotkey dispatches
  // TICKER_SEARCH_OPEN_EVENT on the combobox trigger with the typed character
  // as the seed; it must land in the input rather than be swallowed.
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const onOpenEvent = (e: Event) => {
      const seed = (e as CustomEvent<TickerSearchOpenDetail>).detail?.seed ?? ''
      if (editingRef.current) {
        // Keys typed fast land here too until React has focused the input (the type-to-search
        // hook sees them on the document): they continue the query instead of restarting it,
        // or "AAPL" typed over a panel opened the search on just "L".
        if (seed) {
          seedQueryRef.current += seed
          setQuery(seedQueryRef.current)
          setDebouncedQuery(seedQueryRef.current)
        }
        return
      }
      startEditingRef.current()
      editingRef.current = true
      seedQueryRef.current = seed
      if (seed) {
        seededRef.current = true
        setQuery(seed)
        setDebouncedQuery(seed)
      }
    }
    el.addEventListener(TICKER_SEARCH_OPEN_EVENT, onOpenEvent)
    return () => el.removeEventListener(TICKER_SEARCH_OPEN_EVENT, onOpenEvent)
  }, [])

  // Focus when entering edit mode. Normal opens select the current symbol so
  // typing replaces it; seeded opens keep the seed and park the caret after it.
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      if (seededRef.current) {
        const end = inputRef.current.value.length
        inputRef.current.setSelectionRange(end, end)
        seededRef.current = false
      } else {
        inputRef.current.select()
      }
    }
  }, [editing])

  const selectHighlighted = useCallback(() => {
    const item = results[highlightIndex]
    if (item) {
      selectTicker(item.symbol)
    } else if (query.trim()) {
      const trimmed = query.trim()
      const inverted = invertPrefix && trimmed.startsWith('-')
      const symbol = inverted ? trimmed.slice(1) : trimmed
      onSelect(symbol.toUpperCase(), '')
      if (invertPrefix) onInvertPrefixChange?.(inverted)
      close()
    }
  }, [
    results,
    highlightIndex,
    query,
    selectTicker,
    onSelect,
    close,
    invertPrefix,
    onInvertPrefixChange,
  ])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightIndex((prev) => Math.min(prev + 1, results.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightIndex((prev) => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter') {
        selectHighlighted()
      } else if (e.key === 'Escape') {
        // Consume: closing the search is this Escape's one job; lower links
        // in the chain (maximize restore, replay exit) check defaultPrevented.
        e.preventDefault()
        close()
      }
    },
    [results.length, selectHighlighted, close],
  )

  // Close on click-outside, keeping the current ticker (Chris, 2026-10-07: clicking elsewhere
  // only deactivates the search; Enter or a row chooses a ticker. The 2026-08-19 rule committed
  // the typed text, so a stray letter typed over a panel switched its ticker). Capture phase:
  // neighboring header controls stop propagation (to keep panel-drag from hijacking their
  // clicks), which would starve a bubble-phase listener. `pointerdown`, not `mousedown`: a
  // canvas or grid that calls preventDefault on pointerdown never gets a mousedown at all.
  // With no list showing (an empty input), any press outside the field closes it.
  useEffect(() => {
    if (!editing) return
    const handle = (e: PointerEvent) => {
      const target = e.target as Node
      const inField = containerRef.current?.contains(target) ?? false
      const inList = dropdownRef.current?.contains(target) ?? false
      if (!inField && !inList) closeRef.current()
    }
    document.addEventListener('pointerdown', handle, true)
    return () => document.removeEventListener('pointerdown', handle, true)
  }, [editing])

  // Dropdown position — flips above the field when there is no room below.
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition | null>(null)
  useEffect(() => {
    if (!editing || !containerRef.current) {
      setDropdownPos(null)
      return
    }
    const updatePos = () => {
      const rect = containerRef.current?.getBoundingClientRect()
      if (rect) setDropdownPos(computeDropdownPosition(rect, window.innerHeight))
    }
    updatePos()
    window.addEventListener('scroll', updatePos, true)
    window.addEventListener('resize', updatePos)
    return () => {
      window.removeEventListener('scroll', updatePos, true)
      window.removeEventListener('resize', updatePos)
    }
  }, [editing])

  const isInline = variant === 'inline'
  const showResults = editing && debouncedQuery.length > 0

  return (
    <div
      ref={containerRef}
      // Inline: content-sized (backtest-editor-title style) — hugs the ticker,
      // with a ~4-character floor, growing for longer symbols. The clear (X)
      // variant keeps a slightly wider floor so the button doesn't crowd.
      className={isInline ? 'inline-flex' : 'flex w-full'}
      style={isInline ? { minWidth: showClear ? 92 : undefined } : undefined}
    >
      <div
        // A searchable text input that opens a results listbox — `combobox`, not
        // `button`: it reads as an editable field (text caret + border/ring hover
        // below), never a clickable button (Chris, 2026-08-19).
        role="combobox"
        aria-expanded={showResults}
        aria-controls={listboxId}
        aria-haspopup="listbox"
        data-editing={editing || undefined}
        tabIndex={editing ? -1 : 0}
        onClick={editing ? undefined : startEditing}
        onMouseDown={(e) => e.stopPropagation()}
        onDragStart={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        // draggable=true claims this region as the HTML5 DnD source so the
        // mosaic toolbar ancestor doesn't. onDragStart preventDefault then
        // cancels the drag entirely. draggable=false would NOT work here:
        // Chrome ignores it when an ancestor is draggable=true.
        draggable={true}
        data-companion-label={companionLabel}
        className={cn(
          'flex w-full items-center',
          isInline
            ? 'h-7 gap-1.5 px-2 text-xs text-muted-foreground'
            : 'h-9 gap-2 px-3 text-sm text-muted-foreground',
          !editing &&
            // Rest state reads as an editable text field, NOT a button: a text
            // caret, and hover EMPHASISES THE BORDER/RING instead of filling the
            // background (the button vocabulary). Inline shows an inset ring (no
            // border in the pill); standalone strengthens its border.
            (isInline
              ? 'cursor-text rounded-sm hover:ring-1 hover:ring-inset hover:ring-input'
              : 'cursor-text rounded-md border border-input bg-background hover:border-ring'),
          // Active (editing) state: an inset primary ring marks the focused
          // input. A ring, not a border — no layout shift, and it survives
          // call sites that pass border-0 (e.g. the research title bar).
          editing &&
            (isInline
              ? 'cursor-text rounded-sm ring-1 ring-inset ring-primary/60'
              : 'cursor-text rounded-md border border-transparent bg-background ring-1 ring-inset ring-primary/60'),
          className,
        )}
      >
        <Search className={cn('shrink-0', isInline ? 'h-3 w-3' : 'h-4 w-4')} />
        {editing ? (
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={cn(
              'min-w-0 bg-transparent outline-hidden border-none p-0 text-foreground',
              !isInline && 'flex-1',
            )}
            style={{
              fontSize: 'inherit',
              fontWeight: 'inherit',
              // Inline: ch-sized so the pill grows with what's typed.
              // +1ch slack: `ch` measures the '0' glyph, so wide letters (M/W)
              // overflow an exact-ch box and clip the first character; the
              // extra also mirrors the label's pr-1 so the pill doesn't
              // shrink when entering edit mode.
              ...(isInline ? { width: `${Math.max(4, query.length) + 1}ch` } : {}),
            }}
          />
        ) : (
          <>
            <span
              // Intrinsic width, never clipped: `ch` sizing (still used by the
              // edit-mode input) assumes every glyph is as wide as a zero, so a
              // ticker of wide capitals (MKDW) overflowed its box and rendered
              // as "MKD...". The pill may shift by a few px on entering edit
              // mode; that beats truncating the ticker at rest.
              className={cn(
                'shrink-0 whitespace-nowrap',
                !isInline && 'overflow-hidden text-ellipsis',
                isInline && 'font-medium',
              )}
            >
              {value ?? placeholder}
            </span>
            {showClear && (
              <span
                role="button"
                title="Clear ticker filter"
                onMouseDown={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  onClear?.()
                }}
                className="ml-auto flex shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-hover"
                style={{ width: 16, height: 16 }}
              >
                <X size={12} strokeWidth={2.5} />
              </span>
            )}
          </>
        )}
      </div>
      {showResults &&
        dropdownPos &&
        createPortal(
          <div
            ref={dropdownRef}
            id={listboxId}
            role="listbox"
            // Marks the portal for hover-chrome keep-open checks (the panel
            // header's collapsed-mode reveal watches for this).
            data-ticker-search-portal=""
            onMouseDown={(e) => e.stopPropagation()}
            className="border border-primary/30 bg-popover shadow-2xl shadow-black/40"
            style={{
              position: 'fixed',
              // Exactly one of top/bottom is set — flipped dropdowns anchor by
              // their bottom edge so they stay welded to the field.
              top: dropdownPos.top,
              bottom: dropdownPos.bottom,
              left: dropdownPos.left,
              borderRadius: 6,
              width: 240,
              maxHeight: dropdownPos.maxHeight,
              overflowY: 'auto',
              zIndex: 9999,
            }}
          >
            {results.length > 0 ? (
              results.map((r, idx) => (
                <button
                  key={r.symbol}
                  role="option"
                  aria-selected={idx === highlightIndex}
                  ref={(el) => {
                    if (idx === highlightIndex) el?.scrollIntoView({ block: 'nearest' })
                  }}
                  onClick={() => selectTicker(r.symbol)}
                  onMouseMove={(e) => {
                    // A list that opens under the resting pointer (type-to-search opens it right
                    // where the mouse sits) keeps the top match; only a real move picks a row.
                    const at = pointerRef.current
                    pointerRef.current = { x: e.clientX, y: e.clientY }
                    if (at && (at.x !== e.clientX || at.y !== e.clientY)) setHighlightIndex(idx)
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs',
                    // Primary-tinted wash + thin primary outline: the same
                    // visual language as the grids' .ag-row-active row, dialed
                    // up so the keyboard target is unmistakable (plain accent
                    // was still too subtle on the dark popover background).
                    idx === highlightIndex
                      ? 'bg-primary/20 ring-1 ring-inset ring-primary/20'
                      : 'hover:bg-hover',
                  )}
                >
                  <span
                    className={cn(
                      'w-12 shrink-0 font-semibold',
                      r.symbol === value ? 'text-primary' : 'text-foreground',
                    )}
                  >
                    {r.symbol}
                  </span>
                  <span
                    className={cn(
                      'truncate',
                      idx === highlightIndex ? 'text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    {r.name}
                  </span>
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-xs text-muted-foreground">
                {!loaded ? 'Loading tickers...' : 'No results'}
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  )
}
